import { FC, memo, useContext, useMemo } from 'react';
import { useSnapshot } from 'valtio';
import { UIElement, UIElementType } from '#types/System.types';
import { PageScopeContext } from '../../store';
import FieldUI from './FieldUI';
import ContainerUI from './ContainerUI';
import MetaFieldUI from './MetaFieldUI';
import EntityUI from './EntityUI';
import EmbeddedEntityUI from './EmbeddedEntityUI';
import FormPropsUI from './FormPropsUI';
import AttachmentsUI from './attachments/AttachmentsUI';
import ReferencedListUI from './referencedList';
import ArtifactUI from './ArtifactUI';
import ButtonUI from './ButtonUI';
import { ArTableUI } from '../ArTable';
import { elementVisibility } from './elementVisibility';
import { useVisibilityMode } from '../../use/useVisibilityMode';
import type { ReadOnlyReason } from './readOnlyReason';

/**
 * Props for the LayoutUI component.
 * 
 * LayoutUI is the root rendering component that dispatches UI elements to their
 * appropriate specialized components based on element type.
 */
interface LayoutUIProps {
  /** The UI element to render. Can be a container, field, artifact, table, etc. */
  uiElement: UIElement;
  /** Whether the form/entity is in read-only/locked state. Disables interactive elements. */
  disabled?: boolean;
  /**
   * Why the artifact cannot be written, when that is worth telling the reader.
   *
   * Deliberately narrower than `disabled`, which folds a permission denial
   * together with a user-toggled lock, a host lock, an unloaded record and the
   * PDF export tree. Forwarded verbatim; this component never derives it.
   */
  readOnlyReason?: ReadOnlyReason;
  /**
   * Whether this is a nested container (not the root).
   * 
   * Used to distinguish between the root-level Column (primary container) and nested
   * containers. The root Column does not have width constraints applied, while nested
   * Columns receive maxWidth styling. Defaults to false for root-level elements.
   * 
   * @default false
   */
  isNestedContainer?: boolean;
  /**
   * Maximum width for Row containers in pixels.
   * 
   * Applied to all Row containers to limit their horizontal expansion. If not provided,
   * ContainerUI will use its default value (MAX_LIMITED_WIDTH_VIEWPORT).
   * 
   * @see MAX_LIMITED_WIDTH_VIEWPORT
   */
  rowMaxWidth?: number;
  /**
   * Maximum width for nested Column containers in pixels.
   * 
   * Applied only to nested Column containers (when isNestedContainer is true). The root
   * Column does not receive this constraint. If not provided, ContainerUI will use its
   * default value (MAX_LIMITED_WIDTH_VIEWPORT).
   * 
   * @see MAX_LIMITED_WIDTH_VIEWPORT
   */
  columnMaxWidth?: number;
}

/**
 * LayoutUI - Root component for rendering UI layout structures.
 * 
 * This component serves as the dispatcher for all UI element types. It determines
 * which specialized component should render each element based on its uiElementType.
 * 
 * The component handles:
 * - Visibility filtering based on user permissions and view modes
 * - Recursive rendering of nested container structures
 * - Propagation of width constraints and nested container flags
 * 
 * Production layouts always start with a Column as the root element, though other
 * types are supported for testing purposes.
 * 
 * @param props - The component properties
 * @param props.uiElement - The UI element structure to render
 * @param props.disabled - Whether the form/entity is in read-only mode
 * @param props.readOnlyReason - Why the artifact is read-only, when reportable
 * @param props.isNestedContainer - Whether this is nested (not root) container
 * @param props.rowMaxWidth - Maximum width for Row containers
 * @param props.columnMaxWidth - Maximum width for nested Column containers
 * @returns The rendered UI element or null if not visible
 */
const LayoutUI: FC<LayoutUIProps> = memo(({
  uiElement,
  disabled = false,
  readOnlyReason,
  isNestedContainer = false,
  rowMaxWidth,
  columnMaxWidth,
}) => {
  const pageScope = useSnapshot(useContext(PageScopeContext));
  const insideEntity = pageScope.source === 'entity' && pageScope.entityId !== '';
  const visibilityMode = useVisibilityMode() || (insideEntity ? 'internal' : undefined);
  const isVisible = elementVisibility(uiElement, visibilityMode);
  
  const content = useMemo(() => {
    if (!isVisible) {
      return null;
    }
    
    switch (uiElement.uiElementType) {
      case UIElementType.Field:
        return <FieldUI uiField={uiElement} disabled={disabled} />;
      case UIElementType.EntityReference:
        return <EntityUI uiElement={uiElement} disabled={disabled} />;
      case UIElementType.EmbeddedEntity:
        return <EmbeddedEntityUI uiElement={uiElement} disabled={disabled} />;
      case UIElementType.MetaField:
        return <MetaFieldUI uiMetaField={uiElement} />;
      case UIElementType.FormProperty:
        return <FormPropsUI uiFormProperty={uiElement} />;
      case UIElementType.Attachments:
        return <AttachmentsUI uiAttachments={uiElement} disabled={disabled} readOnlyReason={readOnlyReason} />;
      case UIElementType.ReferencedList:
        return <ReferencedListUI uiReferencedList={uiElement} disabled={disabled} />;
      case UIElementType.Grid:
      case UIElementType.Row:
      case UIElementType.Column:
        return <ContainerUI
          container={uiElement}
          disabled={disabled}
          readOnlyReason={readOnlyReason}
          isNestedContainer={isNestedContainer}
          rowMaxWidth={rowMaxWidth}
          columnMaxWidth={columnMaxWidth}
        />;
      case UIElementType.Artifact:
        return <ArtifactUI uiArtifact={uiElement} disabled={disabled} />;
      case UIElementType.Table:
        return <ArTableUI uiArTable={uiElement} disabled={disabled} />;
      case UIElementType.Button:
        return <ButtonUI uiElement={uiElement} disabled={disabled} />;
      default:
        console.warn(`Unknown UI element type: ${(uiElement as any).uiElementType}`);
        return null;
    }
  }, [uiElement, disabled, readOnlyReason, isVisible, isNestedContainer, rowMaxWidth, columnMaxWidth]);

  return content;
});

LayoutUI.displayName = 'LayoutUI';

export default LayoutUI;